import type { Role, StreamEventDto } from '@pl/domain';

/**
 * What a `user.*` event says in the office's Live activity feed: "Dee W. was added as a lead",
 * "Dee W. was deactivated". The feed puts the admin who did it in front, from `actorName`.
 *
 * The person is named by `payload.name`, their name as the change left it. An email is never shown,
 * even if one somehow reached an event: these events go to every screen watching the whole company,
 * and the API keeps addresses out of them for that reason. Without a name the person is "A user".
 *
 * The feed's `describe.ts` hands any `user.*` type here, first thing in its `what()`.
 */
const ROLE_PHRASE: Record<Role, string> = { worker: 'a worker', lead: 'a lead', office: 'an office user', admin: 'an admin' };

const asRecord = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {});
const words = (v: unknown, fallback: string): string => (typeof v === 'string' && v.trim() && !v.includes('@') ? v : fallback);
const rolePhrase = (r: unknown): string => (typeof r === 'string' && Object.hasOwn(ROLE_PHRASE, r) ? ROLE_PHRASE[r as Role] : 'a user');

/** The line for a `user.*` event, or null for any other event. */
export function userEventWords(e: Pick<StreamEventDto, 'type' | 'payload'>): string | null {
  if (!e.type.startsWith('user.')) return null;
  const p = e.payload;
  const name = words(p.name, 'A user');
  switch (e.type) {
    case 'user.created':
      return `${name} was added as ${rolePhrase(p.role)}`;
    case 'user.deactivated':
      return `${name} was deactivated`;
    case 'user.reactivated':
      return `${name} was reactivated`;
    case 'user.password_reset':
      return `${name}’s password was reset`;
    case 'user.password_changed':
      return `${name} changed their password`;
    case 'user.updated': {
      const before = asRecord(p.before);
      const after = asRecord(p.after);
      const renamed = 'name' in after;
      const role = 'role' in after;
      const sites = 'siteIds' in after;
      const oldName = words(before.name, 'someone else');
      if (renamed && !role && !sites) return `${oldName} was renamed ${name}`;
      if (role && !renamed && !sites) return `${name} is now ${rolePhrase(after.role)}`;
      if (sites && !renamed && !role) return after.siteIds === null ? `${name} can now work at every site` : `${name}’s sites were changed`;
      const parts = [renamed && `renamed from ${oldName}`, role && `now ${rolePhrase(after.role)}`, sites && 'sites changed'].filter(Boolean);
      return parts.length ? `${name} was changed: ${parts.join(', ')}` : `${name} was changed`;
    }
    default:
      return `${name} was changed`;
  }
}
